import { ValidationResult } from '@/core/types/validation-result';

/**
 * Tipos de documentos.
 */
const _documentTypes: Record<string, string> = {
  factura: 'FA',
  pickslip: 'PS',
  pedido: 'PW'
};

/**
 * Longitudes mínimas para los tipos de documentos.
 */
const minLengths = {
  factura: 12,
  pickslip: 6,
  pedido: 11
};

/**
 * Valida un ID de documento según su tipo.
 *
 * @param idType - El tipo de ID.
 *
 * @param idValue - El valor del ID.
 *
 * @returns Un objeto con el resultado de la validación.
 */
export function validateId(idType: string, idValue: string): ValidationResult {
  const cleanedValue = idValue.trim();

  if (!cleanedValue) {
    return { isValid: false, errorMessage: undefined };
  }

  let isValid = false;
  let errorMessage: string | undefined = undefined;

  switch (idType) {
    case 'factura':
      isValid = /^V[A-Z][A-Z0-9]\d([A-Z]{2})?-\d{9}$|^SD[A-Z]{0,2}-\d{10}$/.test(cleanedValue);
      errorMessage = isValid ? undefined : 'placeholders:orders-validation';
      break;
    case 'pedido':
      isValid = /^(?:CW|NW)-\d{9}$|^VK-\d{10}$/.test(cleanedValue);
      errorMessage = isValid ? undefined : 'placeholders:pedidos-validation';
      break;
    case 'pickslip':
      isValid = /^\d+$/.test(cleanedValue);
      errorMessage = isValid ? undefined : 'placeholders:pickslip-validation';
      break;
    default:
      isValid = true;
      errorMessage = undefined;
  }

  return { isValid, errorMessage };
}

/**
 * Obtiene la longitud mínima para un tipo de ID.
 *
 * @param idType - El tipo de ID.
 *
 * @returns La longitud mínima.
 */
export function getMinLength(idType: string): number {
  return minLengths[idType as keyof typeof minLengths] || 0;
}

/**
 * Comprueba si un valor de ID tiene la longitud mínima requerida.
 *
 * @param idType - El tipo de ID.
 *
 * @param idValue - El valor del ID.
 *
 * @returns `true` si el valor tiene la longitud mínima, de lo contrario `false`.
 */
export function hasMinimumLength(idType: string, idValue: string): boolean {
  const minLength = getMinLength(idType);
  return idValue.trim().length >= minLength;
}

/**
 * Obtiene el tipo de documento.
 *
 * @param idType - El tipo de ID.
 *
 * @returns El tipo de documento.
 */
export function getDocumentType(idType: string) {
  return _documentTypes[idType];
}

export type AssociateDocKind = 'web-order' | 'invoice' | null;

export interface AssociatedDoc {
  id: string;
  kind: AssociateDocKind;
  label: string;
}

const WEB_ORDER_PATTERN = /^(?:CW|NW)-\d{9}$|^VK-\d{10}$/;
const SD_INVOICE_PATTERN = /^SD[A-Z]{0,2}-\d{10}$/;
const ALT_INVOICE_PATTERN = /^V[A-Z][A-Z0-9]\d([A-Z]{2})?-\d{9}$/;

export function detectDocKind(value: string): AssociateDocKind {
  const v = value.trim();
  if (WEB_ORDER_PATTERN.test(v)) return 'web-order';
  if (SD_INVOICE_PATTERN.test(v) || ALT_INVOICE_PATTERN.test(v)) return 'invoice';
  return null;
}

function getLabelForKind(kind: AssociateDocKind): string {
  if (kind === 'web-order') return 'orden';
  if (kind === 'invoice') return 'factura';
  return 'documento';
}

const ASSOCIATED_FIELD_CANDIDATES = [
  'webOrder',
  'webOrderId',
  'webOrderNumber',
  'purchaseOrder',
  'purchase_order',
  'purchaseOrderNumber',
  'purchase_order_number',
  'orderNumber',
  'order_number',
  'sourceOrder',
  'source_order',
  'originOrder',
  'origin_order',
  'parentOrder',
  'parent_order',
  'associatedOrder',
  'associated_order',
  'relatedOrder',
  'related_order',
  'linkedOrder',
  'linked_order',
  'web_order',
  'web_order_id',
  'web_order_number',
  'saleOrder',
  'sale_order',
  'salesOrder',
  'sales_order',
  'customerOrder',
  'customer_order',
  'invoice',
  'invoiceId',
  'invoiceNumber',
  'invoice_number',
  'associatedInvoice',
  'associated_invoice',
  'relatedInvoice',
  'related_invoice',
  'linkedInvoice',
  'linked_invoice',
  'sourceInvoice',
  'source_invoice',
  'parentInvoice',
  'parent_invoice',
  'sdInvoice',
  'sd_invoice',
  'deliveryInvoice',
  'delivery_invoice',
  'billingInvoice',
  'billing_invoice'
] as const;

function pickStringValue(
  obj: Record<string, unknown> | null | undefined,
  candidates: readonly string[]
): string | null {
  if (!obj) return null;
  for (const key of candidates) {
    const val = obj[key];
    if (typeof val === 'string' && val.trim().length > 0) {
      return val.trim();
    }
  }
  return null;
}

const MAX_SCAN_DEPTH = 6;

interface ScanResult {
  webOrders: string[];
  invoices: string[];
}

function scanRecordForDocs(
  value: unknown,
  out: ScanResult,
  current: string,
  visited: Set<unknown>,
  depth: number
): void {
  if (depth > MAX_SCAN_DEPTH || value === null || value === undefined) return;
  if (typeof value === 'string') {
    const v = value.trim();
    if (!v || v === current) return;
    if (WEB_ORDER_PATTERN.test(v) && !out.webOrders.includes(v)) {
      out.webOrders.push(v);
    } else if (
      (SD_INVOICE_PATTERN.test(v) || ALT_INVOICE_PATTERN.test(v)) &&
      !out.invoices.includes(v)
    ) {
      out.invoices.push(v);
    }
    return;
  }
  if (typeof value !== 'object') return;
  if (visited.has(value)) return;
  visited.add(value);
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i += 1) {
      scanRecordForDocs(value[i], out, current, visited, depth + 1);
    }
    return;
  }
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj);
  for (let i = 0; i < keys.length; i += 1) {
    scanRecordForDocs(obj[keys[i]], out, current, visited, depth + 1);
  }
}

function fullScanAssociated(
  record: Record<string, unknown> | null | undefined,
  currentKind: AssociateDocKind,
  current: string
): AssociatedDoc | null {
  if (!record) return null;
  const out: ScanResult = { webOrders: [], invoices: [] };
  scanRecordForDocs(record, out, current, new Set<unknown>(), 0);

  if ((!currentKind || currentKind === 'invoice') && out.webOrders.length > 0) {
    const id = out.webOrders[0];
    return { id, kind: 'web-order', label: 'orden' };
  }
  if ((!currentKind || currentKind === 'web-order') && out.invoices.length > 0) {
    const id = out.invoices[0];
    return { id, kind: 'invoice', label: 'factura' };
  }
  return null;
}

/**
 * Detecta el documento asociado de un registro.
 *
 * - Si `currentNumber` es una factura SD/V… → busca orden NW/CW/VK asociada.
 * - Si `currentNumber` es pedido web NW/CW/VK → busca factura SD/V… asociada.
 * - Usa como fallback el valor del contexto de búsqueda si es del tipo opuesto.
 *
 * El schema `OrderSearchResultSchema` usa `.passthrough()`, por lo que cualquier
 * campo adicional del backend se conserva y puede ser leído aquí.
 */
export function getAssociatedDocument(
  record: Record<string, unknown> | null | undefined,
  currentNumber: string | null | undefined,
  searchContext?: { docType?: string; docId?: string }
): AssociatedDoc | null {
  const current = (currentNumber ?? '').trim();
  const currentKind: AssociateDocKind = current ? detectDocKind(current) : null;

  if (record) {
    const firstPass = pickStringValue(record, ASSOCIATED_FIELD_CANDIDATES);
    if (firstPass) {
      const kind = detectDocKind(firstPass);
      if (kind && (!currentKind || kind !== currentKind)) {
        return {
          id: firstPass,
          kind,
          label: getLabelForKind(kind)
        };
      }
    }

    if (!currentKind || currentKind === 'invoice') {
      for (const key of ASSOCIATED_FIELD_CANDIDATES) {
        const val = record[key];
        if (typeof val === 'string' && WEB_ORDER_PATTERN.test(val.trim())) {
          return { id: val.trim(), kind: 'web-order', label: 'orden' };
        }
      }
    }

    if (!currentKind || currentKind === 'web-order') {
      for (const key of ASSOCIATED_FIELD_CANDIDATES) {
        const val = record[key];
        if (
          typeof val === 'string' &&
          (SD_INVOICE_PATTERN.test(val.trim()) || ALT_INVOICE_PATTERN.test(val.trim()))
        ) {
          return { id: val.trim(), kind: 'invoice', label: 'factura' };
        }
      }
    }

    const deepHit = fullScanAssociated(record, currentKind, current);
    if (deepHit) return deepHit;
  }

  if (searchContext?.docId) {
    const searchId = searchContext.docId.trim();
    const searchKind = detectDocKind(searchId);
    if (!searchId || !searchKind) return null;
    if (searchKind === currentKind) return null;
    if (!current) return null;
    return {
      id: searchId,
      kind: searchKind,
      label: getLabelForKind(searchKind)
    };
  }

  return null;
}

/**
 * Extrae el número de pedido web (ej: NW-000142473, CW-XXXXXXXXX, VK-XXXXXXXXXX)
 * asociado a una factura desde los datos crudos del registro.
 *
 * @deprecated Usa `getAssociatedDocument` en su lugar para soportar ambos sentidos.
 */
export function extractAssociatedWebOrder(
  record: Record<string, unknown> | null | undefined,
  fallbackContext?: string
): string | null {
  const assoc = getAssociatedDocument(record, undefined, {
    docType: 'pedido',
    docId: fallbackContext
  });
  if (assoc && assoc.kind === 'web-order') return assoc.id;
  return null;
}
